import {
  ChangeEvent,
  ElementType,
  KeyboardEvent,
  ReactNode,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { uniq } from 'lodash'

import {
  Dialog,
  DialogTrigger,
  DrawerContent,
} from '~/components/component-library/Dialog'
import { TokenSelectorNetworkList } from '~/components/component-library/TokenSelector/TokenSelectorNetworkList'
import {
  NetworkMatch,
  TokenSelectorNetworkMatches,
} from '~/components/component-library/TokenSelector/TokenSelectorNetworkMatches'
import { TokenSelectorNetworkTabs } from '~/components/component-library/TokenSelector/TokenSelectorNetworkTabs'
import { TokenSelectorTokenList } from '~/components/component-library/TokenSelector/TokenSelectorTokenList'
import {
  NetworkFilterState,
  SortBy,
} from '~/components/component-library/TokenSelector/types'
import { useDisplayTokens } from '~/components/component-library/TokenSelector/useDisplayTokens'
import {
  focusElementWithoutScroll,
  restoreScrollPosition,
} from '~/components/component-library/TokenSelector/utils'
import { Input } from '~/components/inputs/Input'
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
} from '~/components/ui/popover'
import { getListedChainIds, SupportedChainId } from '~/constants/chains'
import { useActiveOrgSmartAccountsChainIds } from '~/hooks/org'
import { useAggregatedBalances } from '~/hooks/useAggregatedBalances'
import { useFrequentlyUsedTokens } from '~/hooks/useFrequentlyUsedTokens'
import { useRelayCurrencySearch } from '~/hooks/useRelayCurrencySearch'
import {
  RelayNetworkMetadata,
  useRelayNetworks,
} from '~/hooks/useRelayNetworks'
import { useAllChainIds } from '~/hooks/utils'
import { IAddress, VmType } from '~/types'
import { getChainLabel } from '~/utils/chainLabels'
import { useIsMobile } from '~/utils/devices'
import { cn } from '~/utils/tailwind-utils'

// Relay rows render through NetworkDisplay, which prefers the registry name
// over Relay's displayName. Search filters and selected-state copy must resolve
// the same way or a network can display as one name but only match the other.
const getRelayNetworkLabel = (network: RelayNetworkMetadata): string =>
  getChainLabel(network.id, { relayNetwork: network })

// Cap network-name matches shown above the token list.
const NETWORK_MATCH_LIMIT = 5

function clampIndex(
  length: number,
  index: number,
  setIndex: (i: number) => void,
) {
  if (length === 0) {
    if (index !== 0) setIndex(0)
  } else if (index > length - 1) {
    setIndex(length - 1)
  }
}

export type TokenSelectorPopoverProps = {
  onChange: (token: { address: string; chainId: number }) => void
  /** If provided, only show balances from this smart account. Must be an org account. */
  filterAccountAddress?: IAddress
  /**
   * Trigger node, or a render fn `(Trigger) => node` that splits the click
   * target (what you wrap in `Trigger`) from the anchor (the returned node).
   */
  children: ReactNode | ((Trigger: ElementType) => ReactNode)
  /** Controlled open state */
  open?: boolean
  onOpenChange?: (open: boolean) => void
  /** Show balances in the dropdown list. Defaults to false. */
  showBalances?: boolean
  /** Enable relay network support (adds "More" button to network tabs) */
  allowRelayNetworks?: boolean
  /** Filter tokens shown in the list */
  tokenFilter?: (token: { address: string; chainId: number }) => boolean
  /** Treat tokenFilter as a strict allowlist — apply it to relay-network
   *  tokens and non-active chains too. Defaults to false. */
  strictTokenFilter?: boolean
  /** Filter chains shown in the network tabs */
  chainFilter?: (chainId: number) => boolean
  /** Offer every smart-account chain, not just the org's active ones.
   *  Active chains list first. Defaults to false. */
  includeAllSmartAccountChains?: boolean
  /** Restore focus to trigger on close (used by token-only variant) */
  restoreTriggerFocusOnClose?: boolean
  /** Explicit trigger ref for reliable focus restoration (Safari) */
  triggerRef?: RefObject<HTMLElement | null>
  /** Supported VM types */
  vmTypes?: VmType[]
  /** Tokens to always show first, regardless of balance or frequency */
  promotedTokens?: { address: string; chainId: number }[]
  sortBy: SortBy
}

type SelectorView = 'tokens' | 'networks'

export function TokenSelectorPopover({
  onChange,
  filterAccountAddress,
  children,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  showBalances = false,
  allowRelayNetworks = false,
  tokenFilter,
  strictTokenFilter,
  chainFilter,
  includeAllSmartAccountChains = false,
  restoreTriggerFocusOnClose = false,
  triggerRef,
  vmTypes,
  promotedTokens,
  sortBy,
}: TokenSelectorPopoverProps) {
  const isMobile = useIsMobile()
  const { registerTokenUse, frequentlyUsedTokens } = useFrequentlyUsedTokens()
  const [internalOpen, setInternalOpen] = useState(false)
  const isControlled = controlledOpen !== undefined
  const isOpen = isControlled ? controlledOpen : internalOpen
  const setIsOpen = useCallback(
    (open: boolean) => {
      if (isControlled) controlledOnOpenChange?.(open)
      else setInternalOpen(open)
    },
    [isControlled, controlledOnOpenChange],
  )

  const [searchQuery, setSearchQuery] = useState('')
  const [highlightedIndex, setHighlightedIndex] = useState(0)
  const [networkHighlightedIndex, setNetworkHighlightedIndex] = useState(0)
  const [networkFilter, setNetworkFilter] = useState<NetworkFilterState>({
    type: 'all',
  })
  const [selectorView, setSelectorView] = useState<SelectorView>('tokens')
  const [selectedRelayNetwork, setSelectedRelayNetwork] =
    useState<RelayNetworkMetadata | null>(null)
  // Active chains that overflowed the tab row (reported by the tabs), listed at
  // the top of the "More" menu.
  const [overflowChainIds, setOverflowChainIds] = useState<number[]>([])

  // Fetch relay networks when enabled
  const { data: relayNetworks = [] } = useRelayNetworks({
    enabled: allowRelayNetworks,
    vmTypes,
  })

  const orgChainIds = useActiveOrgSmartAccountsChainIds()
  const allChainIds = useAllChainIds()
  const baseChainIds = useMemo(() => {
    // Active chains lead so their tabs stay inline; the rest overflow into "More".
    if (includeAllSmartAccountChains) {
      return [
        ...orgChainIds,
        ...allChainIds.filter((chainId) => !orgChainIds.includes(chainId)),
      ]
    }
    return orgChainIds.length > 0 ? orgChainIds : getListedChainIds()
  }, [allChainIds, includeAllSmartAccountChains, orgChainIds])
  const activeChainIds = useMemo(
    () => (chainFilter ? baseChainIds.filter(chainFilter) : baseChainIds),
    [baseChainIds, chainFilter],
  )

  const selectedChainFilter =
    networkFilter.type === 'all' ? null : networkFilter.chainId

  const normalizedSearchQuery = searchQuery.trim()
  const inputRef = useRef<HTMLInputElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const highlightedRef = useRef<HTMLDivElement>(null)
  const networkHighlightedRef = useRef<HTMLDivElement>(null)
  const closeFocusTargetRef = useRef<HTMLElement | null>(null)
  const pendingCloseRestoreRef = useRef<{ x: number; y: number } | null>(null)

  // "More networks" lists networks to add, so already-active chains are
  // excluded here rather than in useRelayNetworks — logo/label consumers of
  // that hook need active chains too.
  const filteredRelayNetworks = useMemo(
    () =>
      relayNetworks.filter(
        (network) =>
          !activeChainIds.includes(network.id) &&
          getRelayNetworkLabel(network)
            .toLowerCase()
            .includes(normalizedSearchQuery.toLowerCase()),
      ),
    [relayNetworks, activeChainIds, normalizedSearchQuery],
  )

  // Overflow chains shown at the top of the "More" list, search-filtered.
  const displayOverflowChainIds = useMemo(
    () =>
      overflowChainIds.filter((chainId) =>
        getChainLabel(chainId)
          .toLowerCase()
          .includes(normalizedSearchQuery.toLowerCase()),
      ),
    [overflowChainIds, normalizedSearchQuery],
  )

  // Networks whose name matches the Top-tab search query, shown as distinct
  // selectable items above the token list.
  const matchedNetworks = useMemo<NetworkMatch[]>(() => {
    if (networkFilter.type !== 'all') return []
    if (normalizedSearchQuery.length === 0) return []

    const query = normalizedSearchQuery.toLowerCase()
    const activeMatches = activeChainIds
      .filter((chainId) => getChainLabel(chainId).toLowerCase().includes(query))
      .map((chainId): NetworkMatch => ({ kind: 'active', chainId }))

    const activeIdSet = new Set(activeChainIds)
    const relayMatches = relayNetworks
      .filter(
        (network) =>
          !activeIdSet.has(network.id) &&
          getRelayNetworkLabel(network).toLowerCase().includes(query),
      )
      .map((network): NetworkMatch => ({ kind: 'relay', metadata: network }))

    return [...activeMatches, ...relayMatches].slice(0, NETWORK_MATCH_LIMIT)
  }, [networkFilter.type, normalizedSearchQuery, activeChainIds, relayNetworks])

  const filterAccountAddresses = useMemo(
    () => (filterAccountAddress ? [filterAccountAddress] : undefined),
    [filterAccountAddress],
  )

  const { balances } = useAggregatedBalances({
    filterAccountAddresses,
  })

  // Tab cycling walks the *visible* tabs (Top + inline chains) then "More".
  // Overflowed chains aren't inline tabs — they're reached inside "More".
  const tabOptions = useMemo(
    () =>
      [
        null,
        ...activeChainIds.filter((id) => !overflowChainIds.includes(id)),
      ] as (number | null)[],
    [activeChainIds, overflowChainIds],
  )

  // Determine which chain IDs to search.
  // When relay networks are allowed, the Top tab also unions chains where
  // the user has freq-used tokens so the relay search returns matches there
  // (mirrors the non-search Top tab list).
  const searchChainIds = useMemo(() => {
    if (networkFilter.type !== 'all') return [networkFilter.chainId]
    if (selectedRelayNetwork)
      return [...activeChainIds, selectedRelayNetwork.id]
    if (!allowRelayNetworks) return activeChainIds
    const freqChainIds = frequentlyUsedTokens.map((t) => t.chainId)
    return uniq([...activeChainIds, ...freqChainIds])
  }, [
    networkFilter,
    activeChainIds,
    selectedRelayNetwork,
    allowRelayNetworks,
    frequentlyUsedTokens,
  ])

  const { data: relayResults = [], isFetching: isSearchingRelay } =
    useRelayCurrencySearch({
      chainIds: searchChainIds,
      searchTerm: normalizedSearchQuery,
      enabled: selectorView === 'tokens' && normalizedSearchQuery.length > 0,
      vmTypes,
      scopeToChains: networkFilter.type === 'relay',
    })

  const displayBalances = useMemo(
    () => balances.filter((b) => activeChainIds.includes(b.chainId)),
    [balances, activeChainIds],
  )

  const listItems = useDisplayTokens({
    balances: displayBalances,
    relayResults,
    allowFrequentlyUsedFromNonActiveChains: allowRelayNetworks,
    activeChainIds,
    networkFilter,
    searchQuery: normalizedSearchQuery,
    tokenFilter,
    strictTokenFilter,
    showBalances,
    promotedTokens,
    sortBy,
  })

  // Network matches are navigable above the tokens. The combined
  // arrow-key list is [matches..., tokens...]; the highlight starts on the first
  // token (index === matchCount) and arrow-up reaches the networks.
  const topNetworkMatchCount = matchedNetworks.length
  const tokenNavCount = topNetworkMatchCount + listItems.length

  // The "More" list is navigable as [overflow chains..., relay...].
  const overflowNavCount = displayOverflowChainIds.length
  const networkNavCount = overflowNavCount + filteredRelayNetworks.length

  // A "More" tab exists when there's anything to put in it: relay networks
  // and/or chains that overflowed the tab row.
  const hasMoreTab = allowRelayNetworks || overflowChainIds.length > 0

  useEffect(
    function resetOnClose() {
      if (!isOpen) {
        setSearchQuery('')
        setNetworkFilter({ type: 'all' })
        setHighlightedIndex(0)
        setNetworkHighlightedIndex(0)
        setSelectorView('tokens')
        setSelectedRelayNetwork(null)
        setOverflowChainIds([])
      }
    },
    [isOpen, setNetworkFilter],
  )

  useEffect(
    function defaultToNetworksViewWhenNoActiveChains() {
      if (
        isOpen &&
        activeChainIds.length === 0 &&
        allowRelayNetworks &&
        relayNetworks.length > 0
      ) {
        setSelectorView('networks')
      }
    },
    [isOpen, activeChainIds.length, allowRelayNetworks, relayNetworks.length],
  )

  useEffect(() => {
    if (selectorView === 'tokens') {
      // Default the highlight to the first token, past any matches — but when
      // there are no tokens, land on the first network match instead of letting
      // the clamp pull it to the last one.
      setHighlightedIndex(listItems.length > 0 ? topNetworkMatchCount : 0)
    } else {
      setNetworkHighlightedIndex(0)
    }
  }, [
    normalizedSearchQuery,
    selectorView,
    topNetworkMatchCount,
    listItems.length,
  ])

  useEffect(() => {
    if (selectorView === 'tokens') {
      clampIndex(tokenNavCount, highlightedIndex, setHighlightedIndex)
    }
  }, [selectorView, tokenNavCount, highlightedIndex])

  useEffect(() => {
    if (selectorView === 'networks') {
      clampIndex(
        networkNavCount,
        networkHighlightedIndex,
        setNetworkHighlightedIndex,
      )
    }
  }, [selectorView, networkNavCount, networkHighlightedIndex])

  const selectedChainLabel = useMemo(() => {
    if (selectedChainFilter && activeChainIds.includes(selectedChainFilter))
      return getChainLabel(selectedChainFilter)
    if (networkFilter.type === 'relay' && selectedRelayNetwork)
      return getRelayNetworkLabel(selectedRelayNetwork)
    return
  }, [
    selectedChainFilter,
    selectedRelayNetwork,
    activeChainIds,
    networkFilter.type,
  ])

  useEffect(() => {
    if (!isOpen) return

    const index =
      selectorView === 'tokens' ? highlightedIndex : networkHighlightedIndex

    // On the first item (e.g. after cyclic nav wraps from the end), scroll the
    // container all the way to the top so the category header above it stays
    // visible — scrollIntoView on the row alone would clip it.
    if (index === 0) {
      scrollContainerRef.current?.scrollTo({ top: 0 })
      return
    }

    const ref =
      selectorView === 'tokens' ? highlightedRef : networkHighlightedRef
    ref.current?.scrollIntoView({ block: 'nearest' })
  }, [highlightedIndex, networkHighlightedIndex, selectorView, isOpen])

  /** Cycles through network tabs when user presses Tab/Shift+Tab */
  const cycleTab = useCallback(
    (reverse: boolean) => {
      const isOnRelayTab = networkFilter.type === 'relay'
      // A selected overflowed chain is surfaced in the "More" slot, so it counts
      // as being on the "More" tab for cycling.
      const isSelectedChainOverflowed =
        selectedChainFilter !== null &&
        overflowChainIds.includes(selectedChainFilter)

      let currentIndex: number
      if (
        selectorView === 'networks' ||
        isOnRelayTab ||
        isSelectedChainOverflowed
      ) {
        // Currently on "More" tab (network list, relay tokens, or overflow chain)
        currentIndex = tabOptions.length
      } else {
        currentIndex = tabOptions.indexOf(selectedChainFilter)
      }

      const totalOptions = hasMoreTab
        ? tabOptions.length + 1
        : tabOptions.length

      const nextIndex = reverse
        ? currentIndex <= 0
          ? totalOptions - 1
          : currentIndex - 1
        : currentIndex >= totalOptions - 1
          ? 0
          : currentIndex + 1

      if (hasMoreTab && nextIndex === tabOptions.length) {
        // Cycling to "More" tab
        if (selectedRelayNetwork) {
          setSelectorView('tokens')
          setNetworkFilter({
            type: 'relay',
            chainId: selectedRelayNetwork.id,
            metadata: selectedRelayNetwork,
          })
        } else {
          setSelectorView('networks')
        }
        setHighlightedIndex(0)
        return
      }

      if (selectorView === 'networks') {
        setSelectorView('tokens')
      }

      const nextChainId = tabOptions[nextIndex]
      if (nextChainId === null) {
        setNetworkFilter({ type: 'all' })
      } else {
        setNetworkFilter({ type: 'active', chainId: nextChainId })
      }
      setHighlightedIndex(0)
    },
    [
      tabOptions,
      selectedChainFilter,
      selectedRelayNetwork,
      hasMoreTab,
      overflowChainIds,
      selectorView,
      networkFilter.type,
    ],
  )

  const handleSelect = useCallback(
    (
      token: { address: string; chainId: number },
      { viaKeyboard = false }: { viaKeyboard?: boolean } = {},
    ) => {
      const scrollX = window.scrollX
      const scrollY = window.scrollY
      pendingCloseRestoreRef.current = { x: scrollX, y: scrollY }

      // Restore focus to the trigger (keeps Tab order). On a mouse commit, blur
      // the search box first so the trigger doesn't inherit its keyboard focus
      // ring (a click can't clear it — the option preventDefaults mousedown).
      if (restoreTriggerFocusOnClose) {
        const target = closeFocusTargetRef.current ?? triggerRef?.current
        if (!viaKeyboard) inputRef.current?.blur()
        focusElementWithoutScroll(target ?? null)
      }

      registerTokenUse(token.address, token.chainId as SupportedChainId)
      onChange(token)
      setIsOpen(false)
    },
    [
      onChange,
      registerTokenUse,
      restoreTriggerFocusOnClose,
      setIsOpen,
      triggerRef,
    ],
  )

  const handleNetworkTabSelect = useCallback(
    (chainId: number | null) => {
      if (chainId === null) {
        setNetworkFilter({ type: 'all' })
      } else {
        setNetworkFilter({ type: 'active', chainId })
      }
      setSelectedRelayNetwork(null)
      if (selectorView === 'networks') {
        setSelectorView('tokens')
      }
      setSearchQuery('')
      setHighlightedIndex(0)
    },
    [selectorView, setNetworkFilter],
  )

  const handleMoreClick = useCallback(() => {
    setSearchQuery('')
    if (selectorView === 'networks') {
      // Toggle back to tokens view if a relay network was selected
      if (selectedRelayNetwork) {
        setSelectorView('tokens')
        setNetworkFilter({
          type: 'relay',
          chainId: selectedRelayNetwork.id,
          metadata: selectedRelayNetwork,
        })
      }
    } else {
      setSelectorView('networks')
    }
  }, [selectorView, selectedRelayNetwork, setNetworkFilter])

  const handleNetworkSelect = useCallback(
    (network: NetworkFilterState) => {
      if (network.type === 'relay') {
        setSelectedRelayNetwork(network.metadata)
        setNetworkFilter(network)
      }
      setSelectorView('tokens')
      setSearchQuery('')
      setHighlightedIndex(0)
    },
    [setNetworkFilter],
  )

  // Selecting a network match behaves like selecting that network.
  const handleNetworkMatchSelect = useCallback(
    (match: NetworkMatch) => {
      if (match.kind === 'active') {
        handleNetworkTabSelect(match.chainId)
      } else {
        handleNetworkSelect({
          type: 'relay',
          chainId: match.metadata.id,
          metadata: match.metadata,
        })
      }
    },
    [handleNetworkTabSelect, handleNetworkSelect],
  )

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (open && restoreTriggerFocusOnClose) {
        if (triggerRef?.current) {
          closeFocusTargetRef.current = triggerRef.current
        } else {
          const active = document.activeElement
          closeFocusTargetRef.current =
            active instanceof HTMLElement && active.tagName !== 'BODY'
              ? active
              : null
        }
      }
      setIsOpen(open)
      if (open) setHighlightedIndex(0)
    },
    [restoreTriggerFocusOnClose, setIsOpen, triggerRef],
  )

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Tab') {
        e.preventDefault()
        cycleTab(e.shiftKey)
        return
      }

      if (selectorView === 'networks') {
        // Arrow keys walk [overflow chains..., relay networks...].
        if (e.key === 'ArrowDown') {
          if (networkNavCount === 0) return
          e.preventDefault()
          setNetworkHighlightedIndex((prev) =>
            prev >= networkNavCount - 1 ? 0 : prev + 1,
          )
          return
        }

        if (e.key === 'ArrowUp') {
          if (networkNavCount === 0) return
          e.preventDefault()
          setNetworkHighlightedIndex((prev) =>
            prev <= 0 ? networkNavCount - 1 : prev - 1,
          )
          return
        }

        if (e.key === 'Enter') {
          e.preventDefault()
          // A highlight below overflowCount is an overflow chain.
          if (networkHighlightedIndex < overflowNavCount) {
            const chainId = displayOverflowChainIds[networkHighlightedIndex]
            if (chainId !== undefined) handleNetworkTabSelect(chainId)
            return
          }
          const network =
            filteredRelayNetworks[networkHighlightedIndex - overflowNavCount]
          if (network) {
            handleNetworkSelect({
              type: 'relay',
              chainId: network.id,
              metadata: network,
            })
          }
          return
        }
      } else {
        // Arrow keys walk the combined [matches..., tokens...] list.
        if (e.key === 'ArrowDown') {
          if (tokenNavCount === 0) return
          e.preventDefault()
          setHighlightedIndex((prev) =>
            prev >= tokenNavCount - 1 ? 0 : prev + 1,
          )
          return
        }

        if (e.key === 'ArrowUp') {
          if (tokenNavCount === 0) return
          e.preventDefault()
          setHighlightedIndex((prev) =>
            prev <= 0 ? tokenNavCount - 1 : prev - 1,
          )
          return
        }

        if (e.key === 'Enter') {
          e.preventDefault()
          // A highlight below matchCount is a network match.
          if (highlightedIndex < topNetworkMatchCount) {
            const match = matchedNetworks[highlightedIndex]
            if (match) handleNetworkMatchSelect(match)
            return
          }
          const item = listItems[highlightedIndex - topNetworkMatchCount]
          if (item) {
            handleSelect(
              { address: item.address, chainId: item.chainId },
              { viaKeyboard: true },
            )
          }
        }
      }
    },
    [
      cycleTab,
      handleSelect,
      handleNetworkSelect,
      handleNetworkMatchSelect,
      handleNetworkTabSelect,
      highlightedIndex,
      networkHighlightedIndex,
      listItems,
      matchedNetworks,
      topNetworkMatchCount,
      tokenNavCount,
      filteredRelayNetworks,
      displayOverflowChainIds,
      overflowNavCount,
      networkNavCount,
      selectorView,
    ],
  )

  const handleSearchChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(e.target.value)
      // The tokens-view highlight reset is owned by the
      // query-change effect (it lands on the first token, past any matches).
      if (selectorView === 'networks') {
        setNetworkHighlightedIndex(0)
      }
    },
    [selectorView],
  )

  const sharedContent = (
    <>
      <Input
        ref={inputRef}
        value={searchQuery}
        onChange={handleSearchChange}
        onKeyDown={handleKeyDown}
        isLoading={selectorView === 'tokens' && isSearchingRelay}
        disableWhenLoading={false}
        className="w-full"
        placeholder={
          selectorView === 'networks'
            ? 'Search networks...'
            : `Search ${selectedChainLabel ? `${selectedChainLabel} ` : ''}tokens or paste address...`
        }
        backgroundShadow={false}
        focusRing={false}
      />
      {!(isSearchingRelay && selectorView === 'tokens') && (
        // Prevent Radix tab triggers from stealing focus — keyboard
        // navigation is handled by the search input's keyDown handler.

        <div onFocusCapture={() => focusElementWithoutScroll(inputRef.current)}>
          <TokenSelectorNetworkTabs
            activeChainIds={activeChainIds}
            selectedChainFilter={selectedChainFilter}
            selectedRelayNetwork={selectedRelayNetwork}
            allowRelayNetworks={allowRelayNetworks}
            isNetworksViewActive={selectorView === 'networks'}
            onTabSelect={handleNetworkTabSelect}
            onMoreClick={handleMoreClick}
            onOverflowChange={setOverflowChainIds}
          />
        </div>
      )}
      {/* Hide the list entirely while search results are loading */}
      {!(
        isSearchingRelay &&
        selectorView === 'tokens' &&
        listItems.length === 0
      ) && (
        <div
          ref={scrollContainerRef}
          className={cn(
            'mt-0.5 overflow-y-auto',
            isMobile ? 'max-h-[50vh]' : 'min-h-0 flex-1',
          )}
        >
          {selectorView === 'tokens' ? (
            <>
              {/* Network-name matches above the token list, sharing
                  the same keyboard highlight ([matches..., tokens...]). */}
              <TokenSelectorNetworkMatches
                matches={matchedNetworks}
                highlightedIndex={highlightedIndex}
                highlightedRef={highlightedRef}
                highlightText={normalizedSearchQuery}
                onSelect={handleNetworkMatchSelect}
                onHighlight={setHighlightedIndex}
              />
              {/* Always name the current network so it stays visible even with
                  icon-only tabs (a mis-sent asset on the wrong network is costly). */}
              <div className="flex justify-between px-2 py-1.5 text-xs text-secondary">
                {selectedChainLabel ? `${selectedChainLabel} tokens` : 'Tokens'}
              </div>
              <TokenSelectorTokenList
                items={listItems}
                highlightedIndex={highlightedIndex - topNetworkMatchCount}
                highlightedRef={highlightedRef}
                highlightText={normalizedSearchQuery}
                onSelect={handleSelect}
                onHighlight={(index) =>
                  setHighlightedIndex(index + topNetworkMatchCount)
                }
              />
            </>
          ) : (
            <TokenSelectorNetworkList
              networks={filteredRelayNetworks}
              networkFilter={networkFilter}
              highlightedIndex={networkHighlightedIndex}
              highlightedRef={networkHighlightedRef}
              onSelect={handleNetworkSelect}
              onHighlight={setNetworkHighlightedIndex}
              overflowChainIds={displayOverflowChainIds}
              onSelectChain={handleNetworkTabSelect}
            />
          )}
        </div>
      )}
    </>
  )

  if (isMobile) {
    return (
      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        {typeof children === 'function' ? (
          children(DialogTrigger)
        ) : (
          <DialogTrigger asChild>{children}</DialogTrigger>
        )}
        <DrawerContent
          key={isOpen ? 'open' : 'closed'}
          id="token-selector"
          closeButton={{ enabled: true }}
          dialogTitle="Select token"
          hideTitle
        >
          <div className="px-2 pb-4">{sharedContent}</div>
        </DrawerContent>
      </Dialog>
    )
  }

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange} modal>
      {typeof children === 'function' ? (
        <PopoverAnchor asChild>{children(PopoverTrigger)}</PopoverAnchor>
      ) : (
        <PopoverTrigger asChild>{children}</PopoverTrigger>
      )}
      <PopoverContent
        key={isOpen ? 'open' : 'closed'}
        align="start"
        sideOffset={4}
        // Trigger extends straight down — cancel anchored's scale-in (like SelectInput)
        className="flex !w-[--radix-popover-trigger-width] flex-col gap-0.5 overflow-hidden rounded-lg border-gray-200 bg-white p-1 data-[state=closed]:zoom-out-100 data-[state=open]:zoom-in-100"
        style={{
          maxHeight:
            'calc(var(--radix-popover-content-available-height) - 16px)',
        }}
        onOpenAutoFocus={(e) => {
          e.preventDefault()
          setTimeout(() => {
            focusElementWithoutScroll(inputRef.current)
          }, 0)
        }}
        onCloseAutoFocus={(e) => {
          e.preventDefault()
          const pendingRestore = pendingCloseRestoreRef.current
          pendingCloseRestoreRef.current = null

          if (restoreTriggerFocusOnClose) {
            const target = closeFocusTargetRef.current ?? triggerRef?.current
            if (!target) return

            const scrollX = window.scrollX
            const scrollY = window.scrollY

            focusElementWithoutScroll(target)
            restoreScrollPosition(
              pendingRestore?.x ?? scrollX,
              pendingRestore?.y ?? scrollY,
            )
          } else if (pendingRestore) {
            restoreScrollPosition(pendingRestore.x, pendingRestore.y)
          }
        }}
        onFocusOutside={(e) => {
          e.preventDefault()
          setTimeout(() => {
            focusElementWithoutScroll(inputRef.current)
          }, 0)
        }}
      >
        {sharedContent}
      </PopoverContent>
    </Popover>
  )
}
